import type { ComponentPropsWithoutRef, ReactNode, RefObject } from 'react';

import type { ValueOf } from 'cpool-utils';
import type { DateRange } from 'react-day-picker';

import type { COLOR } from '../../types/Colors';
import type { DropdownOption } from '../../types/Dropdown';
import type { ColorClasses } from '../../utils/Colors';
import type { EventBehaviorProps } from '../../utils/EventBehavior';

// ============================================================================
// Constants
// ============================================================================

export const DROPDOWN_DIRECTION = {
    BOTTOM: 'bottom',
    TOP: 'top',
} as const;

export type DropdownDirection = ValueOf<typeof DROPDOWN_DIRECTION>;

export const DROPDOWN_SIZE = {
    MD: 'md',
    SM: 'sm',
} as const;

export type DropdownSize = ValueOf<typeof DROPDOWN_SIZE>;

export const DROPDOWN_MAX_HEIGHT = 149; // px

// ============================================================================
// Component Props
// ============================================================================

type DropdownMenuCommonProps = Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> & {
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    triggerClassName?: string;
    showBorder?: boolean;
    triggerWidth?: string;
    size?: DropdownSize;
    roundedTrigger?: boolean;
};

type DropdownMenuOptionBaseProps<T = string | number> = DropdownMenuCommonProps & {
    datePicker?: false;
    options: DropdownOption<T>[];
    value?: T | T[];
    onChange?: (value: T | T[]) => void;
    multiple?: boolean;
    color?: COLOR;
    badge?: ReactNode;
    formatSelectedOptions?: (labels: ReactNode[]) => ReactNode;
    noOptionsText?: string;
    renderTrigger?: (props: { isOpen: boolean; selectedValue: T | T[] | undefined }) => ReactNode;
    panelClassName?: string;
    hideCheckmarks?: boolean;
    optionClassName?: string;
    optionElementType?: 'button' | 'div';
    optionAlign?: 'left' | 'center';
    minPanelWidth?: number;
    maxPanelWidth?: number;
    /** Makes the panel at least as wide as the trigger, so the menu spans the full input width. */
    matchTriggerWidth?: boolean;
    roundedPanel?: boolean;
    keepPlaceholder?: boolean;
    scrollable?: boolean;
    keepInitialPanelWidth?: boolean;
    closeOnScroll?: boolean;
    maxHeight?: number;
    hideDivider?: boolean;
};

type DropdownMenuOptionsPropsWithSearch<T = string | number> = DropdownMenuOptionBaseProps<T> & {
    searchable: true;
    searchPlaceholder?: string;
    onSearch?: (searchTerm: string) => void;
};

type DropdownMenuOptionsPropsWithoutSearch<T = string | number> = DropdownMenuOptionBaseProps<T> & {
    searchable?: false;
    searchPlaceholder?: never;
    onSearch?: never;
};

type DropdownMenuDatePickerBaseProps = DropdownMenuCommonProps & {
    datePicker: true;
    color?: never;
    closeOnScroll?: never;
    formatSelectedOptions?: never;
    hideCheckmarks?: never;
    keepInitialPanelWidth?: never;
    keepPlaceholder?: never;
    matchTriggerWidth?: never;
    maxPanelWidth?: never;
    minPanelWidth?: never;
    multiple?: never;
    noOptionsText?: never;
    onChange?: never;
    onSearch?: never;
    optionAlign?: never;
    optionClassName?: never;
    optionElementType?: never;
    options?: never;
    panelClassName?: never;
    renderTrigger?: never;
    roundedPanel?: never;
    scrollable?: never;
    searchable?: never;
    searchPlaceholder?: never;
    value?: never;
};

type DropdownMenuDatePickerSingleProps = DropdownMenuDatePickerBaseProps & {
    datePickerMode?: 'single';
    dateValue?: Date | null;
    onDateChange?: (date: Date | null) => void;
};

type DropdownMenuDatePickerRangeProps = DropdownMenuDatePickerBaseProps & {
    datePickerMode: 'range';
    dateValue?: DateRange | undefined;
    onDateChange?: (range: DateRange | undefined) => void;
};

export type DropdownMenuDatePickerProps = DropdownMenuDatePickerSingleProps | DropdownMenuDatePickerRangeProps;

export type DropdownMenuOptionsProps<T = string | number> =
    | DropdownMenuOptionsPropsWithSearch<T>
    | DropdownMenuOptionsPropsWithoutSearch<T>;

export type DropdownMenuProps<T = string | number> = (DropdownMenuOptionsProps<T> | DropdownMenuDatePickerProps) &
    EventBehaviorProps;

// ============================================================================
// Internal Sub-Component Props
// ============================================================================

export interface SearchProps {
    searchTerm: string;
    searchPlaceholder: string;
    onSearchChange: (term: string) => void;
}

export interface OptionCheckmarkProps {
    multiple: boolean;
    selected: boolean;
    optionColorClasses: ColorClasses | null;
}

export interface PanelOptionProps {
    label: ReactNode;
    description?: ReactNode;
    selected: boolean;
    disabled?: boolean;
    multiple: boolean;
    hideCheckmarks: boolean;
    optionAlign: 'left' | 'center';
    optionClassName?: string;
    optionElementType: 'button' | 'div';
    optionColorClasses: ColorClasses | null;
    onSelect: () => void;
}

export interface PanelProps<T> {
    panelRef: RefObject<HTMLDivElement | null>;
    panelPosition: { x: number; y: number };
    panelClassName?: string;
    roundedPanel: boolean;
    scrollable: boolean;
    searchable?: boolean;
    maxPanelWidth?: number;
    minPanelWidth: number;
    keepInitialPanelWidth: boolean;
    initialPanelWidth: number | null;
    searchTerm: string;
    searchPlaceholder: string;
    onSearchChange: (term: string) => void;
    filteredOptions: DropdownOption<T>[];
    noOptionsText: string;
    multiple: boolean;
    hideCheckmarks: boolean;
    optionAlign: 'left' | 'center';
    optionClassName?: string;
    optionElementType: 'button' | 'div';
    isSelected: (value: T) => boolean;
    onSelect: (value: T) => void;
}

export type OptionsPickerProps<T> = Omit<
    DropdownMenuOptionsProps<T>,
    'open' | 'datePicker' | 'searchPlaceholder' | 'onSearch'
> &
    EventBehaviorProps & {
        controlledOpen?: boolean;
        searchPlaceholder?: string;
        onSearch?: (searchTerm: string) => void;
    };

export interface TriggerProps extends EventBehaviorProps {
    isOpen: boolean;
    displayValue: ReactNode;
    colorClasses: ColorClasses;
    size: DropdownSize;
    roundedTrigger: boolean;
    showBorder: boolean;
    multiple: boolean;
    triggerWidth?: string;
    triggerClassName?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}
